import { Link } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { Award, Users, BookOpen, GraduationCap, ChefHat, Star, ArrowRight, Globe } from "lucide-react";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/ialfv394_cover%20page.jpg",
  culinary: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/4e3jh4f6_WhatsApp%20Image%202026-03-04%20at%204.00.06%20PM.jpeg",
  frontDesk: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/2hjnk04u_WhatsApp%20Image%202026-03-04%20at%204.01.13%20PM.jpeg",
  campus: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/yab3drab_WhatsApp%20Image%202026-03-04%20at%204.01.15%20PM%20%284%29.jpeg",
  graduation: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/d3dbw33j_WhatsApp%20Image%202026-03-04%20at%204.04.57%20PM%20%281%29.jpeg",
};

const benefits = [
  {
    icon: Award,
    title: "100% Placement Assistance",
    desc: "Industry partnerships with top hotel chains ensure our graduates land their dream careers.",
  },
  {
    icon: ChefHat,
    title: "World-Class Labs",
    desc: "State-of-the-art culinary kitchens, front office simulations, and housekeeping labs.",
  },
  {
    icon: Users,
    title: "Expert Faculty",
    desc: "Learn from seasoned hospitality professionals with decades of global experience.",
  },
  {
    icon: BookOpen,
    title: "Industry Curriculum",
    desc: "Curriculum designed in collaboration with hospitality industry leaders and experts.",
  },
  {
    icon: GraduationCap,
    title: "Recognized Degrees",
    desc: "University-affiliated programs recognized by AICTE and leading hospitality bodies.",
  },
  {
    icon: Star,
    title: "Global Exposure",
    desc: "International internship opportunities and cultural exchange programs worldwide.",
  },
];

const stats = [
  { value: 9, suffix: "", label: "Years of Excellence", icon: Award },
  { value: 1000, suffix: "+", label: "Students Trained", icon: GraduationCap },
  { value: 100, suffix: "+", label: "Experienced Staff", icon: Users },
  { value: 95, suffix: "%", label: "Placement Rate", icon: Star },
];

function AnimatedCounter({ target, suffix, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = Math.max(1, Math.floor(target / (duration / 16)));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl pt-24">
          <div className="max-w-3xl">
            <p className="font-body text-sm uppercase tracking-[0.3em] text-white/80 mb-4 animate-fade-in">
              Premier Hotel Management College in Hyderabad
            </p>
            <h1
              data-testid="hero-headline"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up"
            >
              Welcome to Vainavi Educational Institutions
            </h1>
            <p
              data-testid="hero-subheadline"
              className="font-body text-lg md:text-xl text-white/90 leading-relaxed mb-10 max-w-2xl animate-fade-in-up delay-200"
            >
              Shaping the Future of Global Hospitality in Hyderabad. Where passion meets profession, and education transforms into excellence.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
              <Link
                to="/courses"
                data-testid="hero-explore-courses-btn"
                className="bg-crimson text-white px-8 py-3.5 font-body text-sm font-semibold uppercase tracking-wider hover:bg-crimson-hover transition-all duration-300 flex items-center gap-2"
              >
                Explore Courses <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="border-2 border-white text-white px-8 py-3.5 font-body text-sm font-semibold uppercase tracking-wider hover:bg-white hover:text-navy transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section data-testid="stats-section" className="bg-navy py-14">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`} className="text-center group">
                <stat.icon className="w-7 h-7 text-white/40 mx-auto mb-3 group-hover:text-crimson-light transition-colors duration-300" />
                <p className="font-heading text-3xl md:text-5xl font-bold text-white">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </p>
                <p className="font-body text-xs md:text-sm text-white/70 mt-2 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
            {/* Text achievement */}
            <div data-testid="stat-abroad-training" className="text-center group col-span-2 lg:col-span-1">
              <Globe className="w-7 h-7 text-white/40 mx-auto mb-3 group-hover:text-crimson-light transition-colors duration-300" />
              <p className="font-heading text-xl md:text-2xl font-bold text-crimson-light leading-tight">
                Abroad Training
              </p>
              <p className="font-body text-xs md:text-sm text-white/70 mt-2 uppercase tracking-wider">International Placements</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Vainavi */}
      <section data-testid="why-choose-section" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="font-body text-sm uppercase tracking-[0.3em] text-crimson mb-3">Why Choose Us</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy tracking-tight">
              The Vainavi Advantage
            </h2>
            <div className="w-16 h-1 bg-crimson mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                data-testid={`benefit-card-${i}`}
                className="group p-8 bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-crimson/10 text-crimson mb-5 group-hover:bg-crimson group-hover:text-white transition-all duration-300">
                  <b.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-navy mb-3">{b.title}</h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Showcase */}
      <section data-testid="campus-showcase" className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-body text-sm uppercase tracking-[0.3em] text-crimson mb-3">Our Campus</p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy tracking-tight mb-6">
                A Campus Designed for Hospitality Excellence
              </h2>
              <p className="font-body text-base text-slate-600 leading-relaxed mb-6">
                Our sprawling campus in Moosapet, Hyderabad features modern infrastructure
                specifically designed for hospitality education at M.S.P-6, 11-5-150/C,
                Moosapet Rd, Bhavani Nagar. From professional-grade culinary kitchens
                to simulated hotel front offices, every facility is built to give students real-world experience.
              </p>
              <ul className="space-y-3 mb-8">
                {["Professional Culinary Kitchens", "Simulated Hotel Front Office", "Modern Housekeeping Labs", "Bakery & Confectionery Lab", "Multi-purpose Auditorium"].map((item) => (
                  <li key={item} className="flex items-center gap-3 font-body text-sm text-slate-700">
                    <div className="w-1.5 h-1.5 bg-crimson rounded-full flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                data-testid="campus-learn-more-btn"
                className="inline-flex items-center gap-2 bg-crimson text-white px-8 py-3 font-body text-sm font-semibold uppercase tracking-wider hover:bg-crimson-hover transition-all duration-300"
              >
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src={IMAGES.culinary}
                alt="Culinary Lab"
                className="w-full h-48 lg:h-64 object-cover"
                data-testid="campus-img-culinary"
              />
              <img
                src={IMAGES.frontDesk}
                alt="Front Desk Training"
                className="w-full h-48 lg:h-64 object-cover mt-8"
                data-testid="campus-img-frontdesk"
              />
              <img
                src={IMAGES.campus}
                alt="Campus View"
                className="w-full h-48 lg:h-64 object-cover col-span-2"
                data-testid="campus-img-aerial"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="relative py-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.graduation})` }}
        />
        <div className="absolute inset-0 bg-navy/90" />
        <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Begin Your Hospitality Journey Today
          </h2>
          <p className="font-body text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Join thousands of successful alumni who started their careers at Vainavi. Applications for the new academic session are now open.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="cta-apply-btn"
              className="bg-crimson text-white px-10 py-4 font-body text-sm font-semibold uppercase tracking-wider hover:bg-crimson-hover transition-all duration-300"
            >
              Apply Now
            </Link>
            <Link
              to="/courses"
              data-testid="cta-courses-btn"
              className="border-2 border-white text-white px-10 py-4 font-body text-sm font-semibold uppercase tracking-wider hover:bg-white hover:text-navy transition-all duration-300"
            >
              View Courses
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
